import React, { useMemo } from 'react';
import { Badge, Button } from '@/components/ui';
import { t } from '@/lib/i18n';
import {
  Compass,
  Megaphone,
  TrendingUp,
  Package,
  Code,
  Users,
  Landmark,
  Heart,
  Building2,
  Shield,
  Zap,
  Bot,
  User,
} from 'lucide-react';
import { DOMAIN_LIST } from './types';
import type { ActiveDomainEntry } from './types';
import { useRorosDomains } from '@/hooks/roros/domains';
import type { RorosDomainDTO } from '@/types/roros/domains';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Compass,
  Megaphone,
  TrendingUp,
  Package,
  Code,
  Users,
  Landmark,
  Heart,
  Building2,
  Shield,
  Zap,
};

interface StepActiveDomainsProps {
  /** F-Meeting-1 Wave 1: value is now structured ActiveDomainEntry[] */
  value: ActiveDomainEntry[];
  onChange: (entries: ActiveDomainEntry[]) => void;
  autoFocus?: boolean;
}

export function StepActiveDomains({ value, onChange, autoFocus }: StepActiveDomainsProps) {
  const activeDomainIds = value.map((e) => e.domainId);
  const activeCount = activeDomainIds.length;

  // AC-Domains-3: fetch API descriptions so the launchpad widget uses the
  // same source of truth as DomainsPage and DomainDetailPage. Falls back to
  // the static DOMAIN_LIST description while loading (no UI regression on
  // first paint).
  const domainsQuery = useRorosDomains();
  const apiDescriptionMap = useMemo<Record<number, string>>(() => {
    const domains = (domainsQuery.data as RorosDomainDTO[] | undefined) ?? [];
    return Object.fromEntries(domains.map((d) => [d.domainId, d.description]));
  }, [domainsQuery.data]);

  /** Toggle domain active/inactive */
  const handleDomainToggle = (domainId: number, isCurrentlyActive: boolean) => {
    // Strategy domain is always required
    const domain = DOMAIN_LIST.find((d) => d.domain_id === domainId);
    if (domain?.required) return;

    if (isCurrentlyActive) {
      // Remove
      onChange(value.filter((e) => e.domainId !== domainId));
    } else {
      // Add with default ledByAi=false
      onChange([...value, { domainId, ledByAi: false }]);
    }
  };

  /** Toggle AI-VP / Human-VP for a domain */
  const handleAiVpToggle = (domainId: number, ledByAi: boolean) => {
    // Strategy domain always Human-VP — cannot toggle
    if (domainId === 1) return;

    onChange(value.map((e) =>
      e.domainId === domainId ? { ...e, ledByAi } : e,
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{t('Select Active Domains')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('Choose which business domains to activate. For each domain, select whether it will be led by a human VP or an AI VP.')}
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          {activeCount} {t('of')} {DOMAIN_LIST.length} {t('active')}
        </Badge>
      </div>

      <div className="space-y-3">
        {DOMAIN_LIST.map((domain) => {
          const IconComponent = ICON_MAP[domain.icon] || Compass;
          const isActive = activeDomainIds.includes(domain.domain_id);
          const entry = value.find((e) => e.domainId === domain.domain_id);
          const ledByAi = entry?.ledByAi ?? false;
          const isStrategyDomain = domain.domain_id === 1;

          return (
            <div
              key={domain.domain_id}
              className={`rounded-lg border p-4 transition-colors ${
                isActive
                  ? 'border-primary-300 bg-primary-50/50'
                  : 'border-border bg-background hover:border-muted-foreground/30'
              } ${domain.required ? 'opacity-90' : 'cursor-pointer'}`}
            >
              <div className="flex items-start gap-3">
                {/* Domain selection checkbox area */}
                <Button
                  type="button"
                  variant="ghost"
                  className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    isActive
                      ? 'border-primary-600 bg-primary-600'
                      : 'border-border bg-background'
                  } ${domain.required ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-primary-400'}`}
                  onClick={() => handleDomainToggle(domain.domain_id, isActive)}
                  disabled={domain.required}
                  aria-label={isActive ? t('Deactivate domain') : t('Activate domain')}
                  autoFocus={autoFocus && domain.domain_id === 1}
                >
                  {isActive && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  )}
                </Button>

                {/* Domain icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                  isActive ? 'bg-primary-100 text-primary-600' : 'bg-muted text-muted-foreground'
                }`}>
                  <IconComponent className="w-4 h-4" />
                </div>

                {/* Domain info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-foreground">{domain.name()}</span>
                    {domain.required && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0">
                        {t('Required')}
                      </Badge>
                    )}
                  </div>
                  {/* AC-Domains-3: API description when available; static fallback while loading */}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {apiDescriptionMap[domain.domain_id] ?? domain.description()}
                  </p>
                </div>

                {/* AI-VP / Human-VP toggle — only for active domains */}
                {isActive && (
                  <div className="flex-shrink-0 flex items-center gap-1 ml-auto">
                    {/* Human-VP button */}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleAiVpToggle(domain.domain_id, false)}
                      disabled={isStrategyDomain}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        !ledByAi
                          ? 'bg-white border-primary-400 text-primary-700 shadow-sm'
                          : 'bg-transparent border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                      } ${isStrategyDomain ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      title={isStrategyDomain ? t('Strategy is always Human-VP') : t('Human VP leads this domain')}
                    >
                      <User className="w-3 h-3" />
                      {t('Human-VP')}
                    </Button>

                    {/* AI-VP button */}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleAiVpToggle(domain.domain_id, true)}
                      disabled={isStrategyDomain}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        ledByAi
                          ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                          : 'bg-transparent border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                      } ${isStrategyDomain ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      title={isStrategyDomain ? t('Strategy is always Human-VP') : t('AI VP leads this domain')}
                    >
                      <Bot className="w-3 h-3" />
                      {t('AI-VP')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* AI-VP notice */}
      {value.some((e) => e.ledByAi) && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
          <p className="text-xs text-primary-700">
            <strong>{t('AI VP selected:')}</strong>{' '}
            {t('For each domain with AI-VP, a Domain VP AI Operator will be created at setup completion with a default Role Contract.')}
          </p>
        </div>
      )}
    </div>
  );
}
